import React, { useState } from 'react';

import {
  Container,
  FormControl,
  Grid,
  Link,
  Select,
  InputLabel,
  MenuItem,
  Typography,
} from '@material-ui/core';
import useSize from '@react-hook/size';
import { makeStyles } from '@material-ui/core/styles';
import {
  MagnifierContainer,
  MagnifierPreview,
  MagnifierZoom,
} from 'react-image-magnifiers';
import { MOUSE_ACTIVATION } from 'react-input-position';

import Header from '../components/Headers';
import Footer from '../components/Footer';
import Title from '../components/Title';

const useStyles = makeStyles((theme) => ({
  main: {
    textAlign: 'center',
  },
  selectorFromControl: {
    margin: theme.spacing(1),
    minWidth: 120,
    [theme.breakpoints.down('sm')]: {
      marginLeft: theme.spacing(0.25),
      marginRight: theme.spacing(0.25),
    },
  },
  cleanDenoiseSmaller: {
    [theme.breakpoints.down('xs')]: {
      marginTop: 'auto',
    },
  },
  magnifierZoomBox: {
    boxShadow: '0 0 0.2em black',
  },
  magnifierZoomCaption: {
    paddingTop: theme.spacing(1),
  },
  selectText: {
    '&.Mui-focused': {
      color: '#FFB6C1',
    },
  },
  select: {
    '&:before': {
      borderBottom: `1px solid #F3B3B1`,
    },
    '&:after': {
      borderBottom: `3px solid #FFB6C1`,
    },
    '&:hover:not(.Mui-disabled):not(.Mui-focused):not(.Mui-error):before': {
      borderBottom: `2px solid #FFB6C1`,
    },
  },
}));

function DemoPageNR(): React.ReactElement {
  const classes = useStyles();
  const demoPageSections = [
    { title: 'Home', url: '/' },
    { title: 'Noise Models', url: '/demo_nm' },
    { title: 'Denoisers', url: '/demo_nr' },
    { title: 'References', url: '/demo_nr#references' },
  ];
  const authorName = 'Ke-Chi Chang';
  const authorGithubProfile = 'https://github.com/arcchang1236';
  const githubPage = 'https://github.com/arcchang1236/CA-NoiseGAN';
  const paperName = 'Learning Camera-Aware Noise Models';

  return (
    <div className={classes.main}>
      <Header
        title={paperName}
        githubPage={githubPage}
        sections={demoPageSections}
      />
      <Container maxWidth="lg">
        <Title anchor="result_denoise" name="Denoisers" />
        <Typography paragraph align="left">
          We compare the performance of denoisers trained from different noise
          models and real data. Ours + Real use the real data used in training
          our noise models to improve the performance. For each scene with each
          camera, we choose one result for visualization.
          <br />
          There are more results in&thinsp;
          <a href="/CA-NoiseGAN#download">Downloadables</a>
          .
          <br />
          <strong>
            You can click the reference image anywhere to get the corresponding
            results. More, you can hover after click to see the dynamic change.
          </strong>
        </Typography>
        <DenoiserDemoComponent />
        <ReferencesSection />
      </Container>
      <Footer authorName={authorName} githubPage={authorGithubProfile} />
    </div>
  );
}

interface Dictionary<T> {
  [Key: string]: T;
}

export function DenoiserDemoComponent(): React.ReactElement {
  const classes = useStyles();
  const [sceneId, setSceneId] = useState(1);
  const [camera, setCamera] = useState(2);

  const imageUrlPrefix = `${process.env.PUBLIC_URL}/images/denoise`;
  const methodOptions = [
    { name: 'Real Noisy', note: 'noisy' },
    { name: 'Gaussian [6]', note: 'g' },
    { name: 'Poisson Gauss. [4]', note: 'pg' },
    { name: 'NoiseFlow [1]', note: 'nf' },
    { name: 'Real Only', note: 'real' },
    { name: 'Ours', note: 'ours' },
    { name: 'Ours + Real', note: 'realours' },
    { name: 'Ground Truth', note: 'clean' },
  ];
  const cameraOptions = [
    { model: 'Pixel', value: 0, fullVal: 'Pixel-Google-google' },
    { model: 'iPhone', value: 1, fullVal: 'iPhone9,3 back camera' },
    { model: 'Samsung', value: 2, fullVal: 'SM-G925I-samsung-samsung' },
    { model: 'Nexus', value: 3, fullVal: 'Nexus 6-motorola-google' },
    { model: 'LG', value: 4, fullVal: 'LG-H815-LGE-lge' },
  ];
  const sceneOptions = [1, 2, 8];
  const imageSources: Dictionary<Dictionary<string>> = {
    0: { 1: '0800_04', 2: '6400_02', 8: '3200_01' },
    1: { 1: '0800_04', 2: '0100_04', 8: '0100_01' },
    2: { 1: '0800_02', 2: '1600_01', 8: '3200_02' },
    3: { 1: '0800_01', 2: '3200_02', 8: '0400_02' },
    4: { 1: '0800_02', 2: '0100_01', 8: '0400_01' },
  };

  const formatFileName = (note: string): string => {
    const footage = imageSources[camera][sceneId];
    return `${imageUrlPrefix}/${camera}_${sceneId}_${footage}_${note}.jpg`;
  };

  const handleSceneChange = (
    event: React.ChangeEvent<{ value: unknown }>
  ): void => {
    setSceneId(event.target.value as number);
  };
  const handleCameraChange = (
    event: React.ChangeEvent<{ value: unknown }>
  ): void => {
    setCamera(event.target.value as number);
  };

  const targets = React.useMemo<React.RefObject<HTMLDivElement>[]>(
    () => Array.from({ length: 5 }).map(() => React.createRef()),
    []
  );
  const [gridWidth] = useSize(targets[0]);

  return (
    <MagnifierContainer>
      <Grid container spacing={2}>
        <Grid item xs={8} sm={4}>
          <FormControl className={classes.selectorFromControl}>
            <InputLabel id="NR-scene-select-helper-label">
              SIDD Scene
            </InputLabel>
            <Select
              labelId="NR-scene-select-helper-label"
              id="NR-scene-select-helper"
              value={sceneId}
              onChange={handleSceneChange}
            >
              {sceneOptions.map((val) => (
                <MenuItem key={val} value={val}>
                  Scene #{val}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl className={classes.selectorFromControl}>
            <InputLabel id="NR-camera-select-helper-label">Camera</InputLabel>
            <Select
              labelId="NR-camera-select-helper-label"
              id="NR-camera-select-helper"
              value={camera}
              onChange={handleCameraChange}
            >
              {cameraOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.model}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <MagnifierPreview
            imageSrc={formatFileName('noisy')}
            mouseActivation={MOUSE_ACTIVATION.MOUSE_DOWN}
            transitionSpeed={0}
          />
        </Grid>
        <Grid item xs={12} sm={8}>
          <Grid container>
            {methodOptions.map((method, i) => (
              <Grid item xs={3} md={3} key={method.name} ref={targets[i]}>
                <Typography
                  className={classes.magnifierZoomCaption}
                  variant="subtitle2"
                >
                  {method.name}
                </Typography>
                <MagnifierZoom
                  className={classes.magnifierZoomBox}
                  style={{
                    height: gridWidth - 5,
                    width: gridWidth - 5,
                    opacity: '1',
                  }}
                  imageSrc={formatFileName(method.note)}
                  transitionSpeed={0}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </MagnifierContainer>
  );
}

function ReferencesSection(): React.ReactElement {
  const references = [
    {
      number: `[1] `,
      link: `https://arxiv.org/abs/1908.08453`,
      text: `"Noise Flow: Noise Modeling with Conditional Normalizing Flows"`,
      suffix: ` , Abdelrahman Abdelhamed, Marcus A. Brubaker, Michael S. Brown (ICCV 2019)`,
    },
    {
      number: `[2] `,
      link: `https://openaccess.thecvf.com/content_cvpr_2018/papers/Abdelhamed_A_High-Quality_Denoising_CVPR_2018_paper.pdf`,
      text: `"A high-quality denoising dataset for smart-phone cameras"`,
      suffix: ` , Abdelhamed, Abdelrahman and Lin, Stephen and Brown, Michael S (CVPR 2018)`,
    },
    {
      number: `[3] `,
      link: `http://openaccess.thecvf.com/content_CVPRW_2019/papers/NTIRE/Abdelhamed_NTIRE_2019_Challenge_on_Real_Image_Denoising_Methods_and_Results_CVPRW_2019_paper.pdf`,
      text: `"Ntire 2019 challenge on real image denoising: Methods and results"`,
      suffix: ` , Abdelhamed, Abdelrahman and Timofte, Radu and Brown, Michael S (CVPR 2019 Workshop)`,
    },
    {
      number: `[4] `,
      link: `https://ieeexplore.ieee.org/iel5/83/4623174/04623175.pdf?casa_token=lqod14Up1QwAAAAA:HKaCMVvbAJWJRsr00pVzanqSAOeqZBKGeRiQFdpmQ-9qr-scr7fDrUYPWrVCs0V5wH-BfN4gpWgu`,
      text: `"Practical Poissonian-Gaussian noise modeling and fitting for single-image raw-data"`,
      suffix: ` , Foi, Alessandro and Trimeche, Mejdi and Katkovnik, Vladimir and Egiazarian, Karen`,
    },
    {
      number: `[5] `,
      link: `http://openaccess.thecvf.com/content_CVPRW_2019/papers/NTIRE/Liu_Learning_Raw_Image_Denoising_With_Bayer_Pattern_Unification_and_Bayer_CVPRW_2019_paper.pdf`,
      text: `"Learning raw image denoising with bayer pattern unification and bayer preserving augmentation"`,
      suffix: ` , Liu, Jiaming and Wu, Chi-Hao and Wang, Yuzhi and Xu, Qin and Zhou, Yuqian and Huang, Haibin and Wang, Chuan and Cai, Shaofan and Ding, Yifan and Fan, Haoqiang and others (CVPR 2019 Workshop)`,
    },
    {
      number: `[6] `,
      link: `https://ieeexplore.ieee.org/iel7/83/4358840/07839189.pdf?casa_token=0BchtjrwPDgAAAAA:58lK5T8JvhJEJaQ0jMkDfKR5zVdqxywrB-_mUHfDULdCsE5gHx-L1Nb4r2Nm4jx1UAOFvuj0jbRd`,
      text: `"Beyond a gaussian denoiser: Residual learning of deep cnn for image denoising"`,
      suffix: ` , Zhang, Kai and Zuo, Wangmeng and Chen, Yunjin and Meng, Deyu and Zhang, Lei`,
    },
  ];
  return (
    <>
      <Title
        anchor="references"
        name="References"
        fontVariant={{ variant: 'h5' }}
      />
      <Typography align="left" variant="body1" color="inherit" gutterBottom>
        {references.map((reference) => (
          <>
            {reference.number}
            <Link href={reference.link} target="_blank" rel="noopener">
              {reference.text}
            </Link>
            {reference.suffix} <br />
          </>
        ))}
      </Typography>
    </>
  );
}

export default DemoPageNR;
